
import { defineComponent } from "vue";
import signalRService from "./services/SignalRService";
import { isBoss, getCurrentUserRole } from "./utils/auth";
import LookupDataService from "./services/LookupDataService";
import { convertImageToPngDataUrl } from "./utils/imageUtils";

export default defineComponent({
  name: "App",
  components: {},
  devServer: {
    https: true,
  },
  data() {
    return {
      companyName: localStorage.getItem("companyNameForHeader"),
      companyIconFileName: "" + localStorage.getItem("companyIconFile"),
      isLoggedIn: false,
      isBossUser: false,
      isApprover: false,
      isEventsUser: false,
      pendingApprovalsCount: 0,
      unsubscribePendingApprovals: null as (() => void) | null,
      brandingLoaded: false,
      openMobileMenus: [] as string[],
      activeDesktopMenu: "" as string,
      mobileNavOpen: false,
    };
  },
  methods: {
    // Handles clicking parent menu items
    toggleMenu(menuName: string) {
      // Desktop logic: toggle the active sidebar (clicking the open menu again closes it)
      if (window.innerWidth >= 768) {
        this.activeDesktopMenu = this.activeDesktopMenu === menuName ? "" : menuName;
      }
      // Mobile logic: Accordion behavior
      else {
        if (this.openMobileMenus.includes(menuName)) {
          this.openMobileMenus = this.openMobileMenus.filter(m => m !== menuName);
        } else {
          this.openMobileMenus.push(menuName);
        }
      }
    },
    // Hides the left sidebar on desktop when mouse leaves the nav/sidebar area
    hideDesktopMenu() {
      this.activeDesktopMenu = "";
    },
    closeMobileMenus() {
      this.openMobileMenus = [];
      this.activeDesktopMenu = "";
      this.mobileNavOpen = false;
    },
    checkLoginStatus() {
      const token = localStorage.getItem("token");
      this.isLoggedIn = !!token && token.length > 0;
      this.isBossUser = isBoss();
      localStorage.setItem("isLogginSuccessfull", this.isLoggedIn ? "true" : "false");

      if (this.isLoggedIn) {
        signalRService.connect((window as any).VUE_APP_API_KEY);
        this.loadApproverStatus();
        this.loadEventsAccessStatus();
        if (!LookupDataService.getData()) {
          // Resuming a session with an already-saved token skips LoginView's
          // own fetch, so lookupData would otherwise never get populated.
          LookupDataService.fetchDataOnLogin((window as any).VUE_APP_API_KEY, token as string)
            .catch((error) => console.error("Failed to refresh lookup data on session resume:", error));
        }
        if (!this.unsubscribePendingApprovals) {
          this.unsubscribePendingApprovals = signalRService.onPendingApprovalsChanged(() => {
            if (this.isApprover) {
              this.loadPendingApprovalsCount();
            }
          });
        }
      } else {
        signalRService.disconnect();
      }
    },
    async loadApproverStatus() {
      try {
        const response = await fetch((window as any).VUE_APP_API_KEY + "SecurityOfficers/NonAvailability/ApproverRoles", {
          method: "GET",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") }
        });
        if (response.ok) {
          const approverRoles: string[] = await response.json();
          this.isApprover = approverRoles.includes(getCurrentUserRole() ?? "");
          if (this.isApprover) {
            await this.loadPendingApprovalsCount();
          }
        }
      } catch (error) {
        console.error("Failed to load holiday approver roles:", error);
      }
    },
    async loadEventsAccessStatus() {
      try {
        const response = await fetch((window as any).VUE_APP_API_KEY + "Events/AllowedRoles", {
          method: "GET",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") }
        });
        if (response.ok) {
          const allowedRoles: string[] = await response.json();
          this.isEventsUser = allowedRoles.includes(getCurrentUserRole() ?? "");
        }
      } catch (error) {
        console.error("Failed to load Events allowed roles:", error);
      }
    },
    async loadPendingApprovalsCount() {
      try {
        const response = await fetch((window as any).VUE_APP_API_KEY + "SecurityOfficers/NonAvailability/PendingApprovals", {
          method: "GET",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") }
        });
        if (response.ok) {
          const pending = await response.json();
          this.pendingApprovalsCount = Array.isArray(pending) ? pending.length : 0;
        }
      } catch (error) {
        console.error("Failed to load pending holiday approvals count:", error);
      }
    },
    logout() {
      this.isLoggedIn = false;
      this.isBossUser = false;
      this.isApprover = false;
      this.isEventsUser = false;
      this.pendingApprovalsCount = 0;
      localStorage.removeItem("token");
      localStorage.setItem("isLogginSuccessfull", "false");
      signalRService.disconnect();
    },
    setLoginStatus(message: boolean) {
      this.isLoggedIn = message;
      localStorage.setItem("isLogginSuccessfull", message ? "true" : "false");
    },
    async loadBranding() {
      try {
        const tenantId = window.location.hostname.split('.').shift()?.toUpperCase();
        const requestUrl = (window as any).VUE_APP_API_KEY + "Login/Settings";
        
        const response = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantId || ''
          }
        });
        
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        
        const branding = await response.json();

        (window as any).SITE_TITLE = branding.branding.companyName;
        (window as any).COMPANY_NAME = branding.branding.companyName;
        (window as any).COMPANY_NAME_FOR_HEADER = branding.branding.companyName;
        (window as any).ICON_FILE = branding.faviconFile;
        (window as any).WHATSAPP_NUMBER = branding.branding.mobileNo;

        localStorage.setItem("companyNameForHeader", branding.branding.companyName);
        localStorage.setItem("companyIconFile", branding.faviconFile);
        localStorage.setItem("whatsappNumber", branding.branding.mobileNo);

        // Pre-converted once here (icon rarely changes) so PDF generation can
        // read it synchronously instead of re-converting on every print.
        if (branding.faviconFile) {
          convertImageToPngDataUrl(branding.faviconFile).then(pngDataUrl => {
            if (pngDataUrl) localStorage.setItem("companyIconPng", pngDataUrl);
            else localStorage.removeItem("companyIconPng");
          });
        } else {
          localStorage.removeItem("companyIconPng");
        }
        localStorage.setItem("companyName", branding.branding.companyName);
        localStorage.setItem("companyStreetAddress", branding.branding.streetAdress || "");
        localStorage.setItem("companyTown", branding.branding.townName || "");
        localStorage.setItem("companyCity", branding.branding.cityName || "");
        localStorage.setItem("companyPostCode", branding.branding.postCode || "");

        this.companyName = branding.branding.companyName;
        this.companyIconFileName = branding.faviconFile;

        document.title = branding.branding.companyName;
        this.brandingLoaded = true;
      } catch (error) {
        console.error("Failed to load branding:", error);
        this.brandingLoaded = true;
      }
    }
  },
  computed: {
    token() {
      return localStorage.getItem("token");
    }
  },
  watch: {
    token(newToken, oldToken) {
      if (newToken !== oldToken) {
        this.checkLoginStatus();
      }
    }
  },
  mounted() {
    this.checkLoginStatus();
    window.addEventListener('login-status-changed', this.checkLoginStatus);
    window.addEventListener('storage', (event) => {
      if (event.key === 'token') {
        this.checkLoginStatus();
      }
    });
    this.loadBranding();
  },
  beforeUnmount() {
    window.removeEventListener('login-status-changed', this.checkLoginStatus);
    window.removeEventListener('storage', this.checkLoginStatus);
    if (this.unsubscribePendingApprovals) {
      this.unsubscribePendingApprovals();
    }
  }
});
